//#region IMPORT
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import M_EIS_NEW from './M_EIS_NEW';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
import { EquipmentActions } from '../../../../Reducer/EquipmentReducer';
import dayjs from 'dayjs';
import { useSearch1, useSearch2, useSave1 } from './M_EIS_NEW_Query';
import { useSave } from '../../../../Hooks/useSave';
import useFileUpload from '../../../../Hooks/useFileUpload';
//#endregion

function M_EIS_NEWF() {
  //#region 변수
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const { dateFrom, dateTo } = useSelector((state) => state.equipment);
  const dispatch = useDispatch();

  const [registeredEquipList, setRegisteredEquipList] = useState([]);
  const [selectedEquips, setSelectedEquips] = useState([]);
  const [custList, setCustList] = useState([]);
  const [selectedCustCd, setSelectedCustCd] = useState('');
  const signatureCanvas = useRef(null);
  const [signInputs, setSignInputs] = useState({ mapUrl: '', mapFiles: {} });

  const searchRef = useRef({
    startDate: dateFrom,
    endDate: dateTo,
    searchDate: dayjs().format('YYYY-MM-DD'),
    coList: [],
    coCd: '',
    custList: [],
    custCd: '',
    depList: [],
    depCd: '',
    searchText: '',
    currentYear: dayjs().format('YYYY'),
    currentMonth: dayjs().format('MM'),
  });

  const signUpload = useFileUpload({
    oldFilePathField: 'issuSignPath',
    newFileNameField: 'saveName',
    getTargetPath: () =>
      `Business/${userInfo.custCd || searchRef.current.custCd}/PPE_BASIC_01`,
  });
  //#endregion

  //#region 이벤트
  useEffect(() => {
    Init();
    return () => {
      signUpload.clearFiles();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (registeredEquipList?.length > 0) {
      const signedEquipIds = registeredEquipList
        .filter((equip) => equip.issuSign === 'Y')
        .map((equip) => equip.issuId?.toString())
        .filter((id) => id);
      if (signedEquipIds.length > 0) {
        setSelectedEquips((prev) => [...new Set([...prev, ...signedEquipIds])]);
      }
    }
  }, [registeredEquipList]);

  const Init = async () => {
    try {
      searchRef.current.startDate = dateFrom;
      searchRef.current.endDate = dateTo;
      searchRef.current.custCd = userInfo.custCd || '';
      setSelectedCustCd(userInfo.custCd || '');
      await Search1Async();
      await Search2Async({
        dateFrom: searchRef.current.startDate || '',
        dateTo: searchRef.current.endDate || '',
        custCd: searchRef.current.custCd || '',
      });
    } catch (e) {
      console.log('Error: ', e);
      dispatch(showSnackbar({ message: e.message, type: 'error' }));
    }
  };
  //#endregion

  //#region 쿼리
  const { runAsync: Search1Async } = useSearch1({
    onSuccess: (rows) => {
      setCustList(rows);
      if (rows.length > 0 && !selectedCustCd) {
        setSelectedCustCd(rows[0].custCd);
        searchRef.current.custCd = rows[0].custCd;
      }
    },
  });

  const { runAsync: Search2Async } = useSearch2({
    onSuccess: (rows) => {
      setRegisteredEquipList(rows);
      SyncEquipReceiptAlarm(rows);
    },
  });
  const { runAsync: Save1Async } = useSave1();

  const { run: Save1 } = useSave({
    menuId: 'M_EIS_NEW',
    fileSave: async () => {
      const signFinalPath = await signUpload.SaveLogicFile();

      return {
        issuSignPath: signFinalPath,
      };
    },
    saveData: () =>
      registeredEquipList.filter((equip) => {
        const equipId = equip.issuId?.toString();
        const isSelected = equipId && selectedEquips.includes(equipId);
        const isNotSigned = equip.issuSign !== 'Y';
        return isSelected && isNotSigned;
      }),
    requestSave: (rows) => Save1Async(rows),
    onSuccess: () => {
      Search2Async({
        dateFrom: searchRef.current.startDate || '',
        dateTo: searchRef.current.endDate || '',
        custCd: searchRef.current.custCd || '',
      });
      setSignInputs({ mapUrl: '', mapFiles: {} });
      signUpload.clearFiles();
      dispatch(BottomSheetActions.CloseBottomSheet());
    },
  });
  //#endregion

  //#region 이벤트 핸들러
  const HandleCustChange = (cust) => {
    const custCd = cust?.custCd || cust?.value || '';
    if (custCd) {
      setSelectedCustCd(custCd);
      searchRef.current.custCd = custCd;
    }
  };

  const HandleSearch = () => {
    const nextDateFrom = searchRef.current.startDate || '';
    const nextDateTo = searchRef.current.endDate || '';

    dispatch(
      EquipmentActions.setEquipReceiptDateRange({
        dateFrom: nextDateFrom,
        dateTo: nextDateTo,
      })
    );

    Search2Async({
      dateFrom: nextDateFrom,
      dateTo: nextDateTo,
      custCd: searchRef.current.custCd || '',
    });
  };

  const HandleFilterClick = () => {
    dispatch(
      BottomSheetActions.SetBottomSheetState({
        bottom: true,
        type: 'EquipIssuanceFilter',
      })
    );
  };

  const HandleEquipToggle = (equipId) => {
    setSelectedEquips((prev) =>
      prev.includes(equipId)
        ? prev.filter((id) => id !== equipId)
        : [...prev, equipId]
    );
  };

  const HandleSignClick = () => {
    if (selectedEquips.length === 0) {
      dispatch(
        showSnackbar({
          message: '실제로 받은 보호구를 선택해주세요.',
          type: 'warning',
        })
      );
      return;
    }
    dispatch(
      BottomSheetActions.SetBottomSheetState({
        bottom: true,
        type: 'EquipIssuanceSign',
      })
    );
  };
  //#endregion

  // 보호구 수령현황 조회 후 뱃지 동기화
  const SyncEquipReceiptAlarm = (rows) => {
    const hasPendingReceipt = rows?.some((equip) => equip.issuSign !== 'Y');
    dispatch(
      EquipmentActions.setEquipReceiptAlarm(hasPendingReceipt ? '수령하기' : '')
    );
  };

  return (
    <div className="wrap">
      <M_EIS_NEW
        userInfo={userInfo}
        registeredEquipList={registeredEquipList}
        selectedEquips={selectedEquips}
        signatureCanvas={signatureCanvas}
        searchRef={searchRef}
        custList={custList}
        selectedCustCd={selectedCustCd}
        signUpload={signUpload}
        onCustChange={HandleCustChange}
        onEquipToggle={HandleEquipToggle}
        onSignClick={HandleSignClick}
        onSave={Save1}
        onFilterClick={HandleFilterClick}
        onSearch={HandleSearch}
        signInputs={signInputs}
        setSignInputs={setSignInputs}
      />
    </div>
  );
}

export default M_EIS_NEWF;
